import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { Search, X } from 'lucide-react';
import { ADMIN_TAB_GROUPS, findAdminTab, type AdminTab } from './adminTabs';

/**
 * Admin area shell — a single "Admin" nav entry opens this quick-navigate
 * vertical-tabs surface (ported from iPlan4's admin section): a left rail
 * with a filter-as-you-type search box above grouped vertical tabs, and the
 * active tab's page rendered in the content area via the router outlet.
 *
 * Scrolling and outer padding come from RootLayout's `<main>`; this component
 * only lays out the rail + content row.
 */
export function AdminLayout(): ReactNode {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const activeTabKey = findAdminTab(location.pathname)?.tab.key;

  // Filter groups/tabs by search term; groups with no matching tabs are hidden.
  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return ADMIN_TAB_GROUPS;
    return ADMIN_TAB_GROUPS.map((group) => ({
      ...group,
      tabs: group.tabs.filter(
        (tab) =>
          tab.label.toLowerCase().includes(term) || group.label.toLowerCase().includes(term),
      ),
    })).filter((group) => group.tabs.length > 0);
  }, [search]);

  const handleTabClick = (tab: AdminTab): void => {
    void navigate(tab.href);
  };

  return (
    <div className="flex min-h-full w-full flex-row">
      {/* Grouped vertical-tab rail */}
      <div className="flex w-56 shrink-0 flex-col border-r border-border">
        {/* Quick-navigate search */}
        <div className="shrink-0 pr-3 pb-1">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search admin"
              aria-label="Search admin"
              className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-8 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Grouped tabs */}
        <nav
          role="tablist"
          aria-orientation="vertical"
          aria-label="Admin sections"
          className="flex flex-1 flex-col items-stretch py-1"
        >
          {filteredGroups.map((group, gi) => (
            <div key={group.key}>
              {gi > 0 && <div className="my-1.5 mr-3 border-t border-border" />}
              <div className="pr-3 pt-2 pb-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.label}
                </span>
              </div>
              {group.tabs.map((tab) => {
                const isActive = tab.key === activeTabKey;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-state={isActive ? 'active' : 'inactive'}
                    onClick={() => handleTabClick(tab)}
                    className={
                      'relative -mr-px inline-flex w-full cursor-pointer select-none items-center justify-start gap-1.5 whitespace-nowrap rounded-r-md border-l-2 px-3 py-2 text-left text-sm font-medium outline-none transition-colors ' +
                      (isActive
                        ? 'border-primary bg-accent/50 text-foreground'
                        : 'border-transparent text-muted-foreground hover:bg-accent/50 hover:text-foreground')
                    }
                  >
                    <Icon className="size-4" aria-hidden />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </div>

      {/* Active tab content */}
      <div className="min-w-0 flex-1 pl-6">
        <Outlet />
      </div>
    </div>
  );
}
